import Phaser from 'phaser';
import { t } from '@i18n/index';
import { formatPosition } from '@core/geo';
import { PALETTE, TEXT_COLOUR, FONT, readoutStyle, px, motionMs } from '../theme';
import { layoutOf, watchResize } from '../ui/layout';
import type { HudState } from './WorldMapScene';
import type { Campaign } from '@core/campaign/campaign';

/**
 * Anzeigen ueber der Weltkarte.
 *
 * Als eigene Szene, weil Objekte der Kartenszene zwangslaeufig mit der Kamera
 * skalieren — bei Zoomstufen zwischen 1 und 100 waere Text dort entweder
 * unlesbar klein oder bildschirmfuellend. Eine zweite Szene hat ihre eigene
 * Kamera und bleibt davon unberuehrt.
 */

/** Registry-Schluessel, unter denen die Kartenleiste ihren Platzbedarf meldet. */
export const HEADER_HEIGHT_KEY = 'layout.headerHeight';
export const ACTION_BAR_HEIGHT_KEY = 'layout.actionBarHeight';

const ROWS: readonly (readonly [string, string])[] = [
  ['position', 'map.hud.position'],
  ['depth', 'map.hud.depth'],
  ['speed', 'map.hud.speed'],
  ['remaining', 'map.hud.remaining'],
  ['fuel', 'voyage.fuel'],
  ['sea', 'map.hud.sea'],
  ['forecast', 'briefing.forecast'],
];

export class HudScene extends Phaser.Scene {
  private rows = new Map<string, Phaser.GameObjects.Text>();
  /** Alles, was nur waehrend einer Fahrt zu sehen sein soll. */
  private panelObjects: (Phaser.GameObjects.Text | Phaser.GameObjects.Rectangle)[] = [];
  private panelVisible = false;
  private lastState: HudState | null = null;

  constructor() {
    super('Hud');
  }

  /** Siehe WorldMapScene.init(): Szenen-Instanzen werden wiederverwendet. */
  init(): void {
    this.rows = new Map();
    this.panelObjects = [];
    this.lastState = null;
  }

  /**
   * Zeigt oder verbirgt die Fahrtanzeige.
   *
   * Im Lagebild faehrt niemand, und dann stuenden hier sieben Zeilen mit
   * Gedankenstrichen. Die Einblendungen fuer neue und verstrichene Notrufe
   * bleiben davon unberuehrt — die gelten auch im Stillstand.
   */
  setPanelVisible(visible: boolean): void {
    this.panelVisible = visible;
    for (const object of this.panelObjects) object.setVisible(visible);
  }

  /**
   * Blendet eine kurze Meldung ein.
   *
   * Zwei Toenungen: Ein neuer Notruf ist ein Alarm — rot, kurz, dringend. Ein
   * verstrichener ist kein Alarm mehr, sondern eine Nachricht, und die soll
   * nicht schreien. Sie steht laenger und in gedaempfter Schrift.
   *
   * Sie sitzt direkt unter der Kopfleiste der Karte — deren Hoehe haengt an
   * Schriftgroesse und Bildschirmbreite und wird deshalb erfragt statt geraten.
   */
  announce(message: string, grave = false): void {
    const layout = layoutOf(this);
    const header = (this.registry.get(HEADER_HEIGHT_KEY) as number | undefined) ?? 80;

    const text = this.add
      .text(layout.centreX, header + 12, message, {
        ...readoutStyle(12),
        color: grave ? PALETTE.inkDim : TEXT_COLOUR.danger,
        backgroundColor: '#0a1119',
        padding: { x: 14, y: 8 },
        align: 'center',
        wordWrap: { width: layout.width - layout.margin * 2 - 28 },
      })
      .setOrigin(0.5, 0)
      .setAlpha(0);

    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: motionMs(220) || 1,
      yoyo: true,
      hold: grave ? 5200 : 3200,
      onComplete: () => text.destroy(),
    });
  }

  create(): void {
    this.build();

    /*
     * Sichtbarkeit selbst bestimmen, nicht von aussen setzen lassen.
     *
     * `scene.launch()` ist asynchron: Ruft die Kartenszene direkt danach
     * `setPanelVisible()`, ist `create()` hier noch nicht gelaufen und die
     * Liste leer — der Balken blieb stehen. Die Szene fragt deshalb selbst,
     * sobald sie steht.
     */
    const campaign = this.registry.get('campaign') as Campaign | undefined;
    this.setPanelVisible((campaign?.activeMissions.length ?? 0) > 0);

    const worldMap = this.scene.get('WorldMap');
    worldMap.events.on('hud', this.render, this);

    // Die Tafel weicht der Bedienleiste aus, deren Hoehe sich mit dem Inhalt
    // aendert — etwa wenn sie im Hochformat auf zwei Zeilen umbricht.
    const onInset = (): void => this.rebuild();
    this.registry.events.on(`changedata-${ACTION_BAR_HEIGHT_KEY}`, onInset);
    watchResize(this, () => this.rebuild());

    // Ohne dieses Aufräumen blieben die Listener beim Szenenwechsel hängen.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      worldMap.events.off('hud', this.render, this);
      this.registry.events.off(`changedata-${ACTION_BAR_HEIGHT_KEY}`, onInset);
    });
  }

  private rebuild(): void {
    for (const object of this.panelObjects) object.destroy();
    this.children.getAll('name', 'vignette').forEach((object) => object.destroy());
    this.rows = new Map();
    this.panelObjects = [];
    this.build();
    this.setPanelVisible(this.panelVisible);
    if (this.lastState !== null) this.render(this.lastState);
  }

  /**
   * Die Fahrtanzeige.
   *
   * Breit genug: eine Spalte unten links. Im Hochformat oder auf einem
   * schmalen Fenster ueber die ganze Breite und in zwei Spalten — eine
   * Tafel von 330 Pixeln haette dort die halbe Karte verdeckt.
   */
  private build(): void {
    const layout = layoutOf(this);
    this.drawVignette(layout.width, layout.height);

    const bottomInset =
      ((this.registry.get(ACTION_BAR_HEIGHT_KEY) as number | undefined) ?? 60) + 8;
    const twoColumns = layout.compact;
    const panelWidth = twoColumns ? layout.width - 16 : 330;
    const columns = twoColumns ? 2 : 1;
    const perColumn = Math.ceil(ROWS.length / columns);
    const columnWidth = (panelWidth - 14 * (columns + 1)) / columns;

    // Zeilenhoehe aus der tatsaechlichen Schrift, nicht fest 21 Pixel — die
    // liefen bei vergroesserter Schrift ineinander.
    const probe = this.add.text(0, 0, 'Mg', { fontFamily: FONT.mono, fontSize: px(11) });
    const rowHeight = Math.round(probe.height + 6);
    probe.destroy();

    const panelHeight = perColumn * rowHeight + 20;
    const panelX = twoColumns ? 8 : 16;
    const panelY = layout.height - panelHeight - bottomInset;

    const panel = this.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, PALETTE.panel, 0.82)
      .setOrigin(0, 0);
    panel.setStrokeStyle(1, PALETTE.panelEdge);
    this.panelObjects.push(panel);

    ROWS.forEach(([key, labelKey], index) => {
      const column = Math.floor(index / perColumn);
      const left = panelX + 14 + column * (columnWidth + 14);
      const y = panelY + 10 + (index % perColumn) * rowHeight;

      this.panelObjects.push(
        this.add.text(left, y, t(labelKey), {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: PALETTE.inkFaint,
        }),
      );
      const value = this.add
        .text(left + columnWidth, y, '—', {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: PALETTE.ink,
        })
        .setOrigin(1, 0);
      this.rows.set(key, value);
      this.panelObjects.push(value);
    });
  }

  /**
   * Weiche Abdunklung zu den Raendern hin.
   *
   * Bindet den Blick in der Bildmitte und nimmt der grossen, gleichmaessig
   * hellen Kartenflaeche die Haerte. Als Farbverlauf auf einer eigenen Textur,
   * weil Phasers Graphics keine Verlaeufe zeichnet.
   */
  private drawVignette(width: number, height: number): void {
    const key = 'hud-vignette';
    if (this.textures.exists(key)) this.textures.remove(key);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(2, Math.round(width / 4));
    canvas.height = Math.max(2, Math.round(height / 4));

    const context = canvas.getContext('2d');
    if (context === null) return;

    const gradient = context.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      Math.min(canvas.width, canvas.height) * 0.35,
      canvas.width / 2,
      canvas.height / 2,
      Math.max(canvas.width, canvas.height) * 0.75,
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.55)');

    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    this.textures.addCanvas(key, canvas);
    this.add
      .image(0, 0, key)
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(-1)
      .setName('vignette');
  }

  private render(state: HudState): void {
    this.lastState = state;
    this.rows.get('position')?.setText(formatPosition(state.position));

    this.rows
      .get('depth')
      ?.setText(
        state.aground
          ? t('map.hud.depth.aground')
          : t('map.hud.metres', { metres: Math.round(state.depthMetres) }),
      )
      .setColor(state.aground ? TEXT_COLOUR.danger : PALETTE.ink);

    this.rows
      .get('speed')
      ?.setText(
        state.underway ? t('map.hud.knots', { knots: Math.round(state.speedKnots) }) : '—',
      );

    this.rows
      .get('remaining')
      ?.setText(t('map.hud.distance', { miles: Math.round(state.remainingNm) }));

    this.rows.get('fuel')?.setText(String(Math.round(state.fuel)));
    this.rows.get('sea')?.setText(t(state.seaStateKey));

    // Die Vorschau ist der eigentliche Spannungsträger unterwegs: Sie kippt,
    // wenn das Wetter die Fahrt bremst.
    const forecast = this.rows.get('forecast');
    forecast?.setText(t(state.projectedTier));
    forecast?.setColor(
      state.projectedTier === 'outcome.rescue'
        ? TEXT_COLOUR.base
        : state.projectedTier === 'outcome.nothing'
          ? TEXT_COLOUR.danger
          : TEXT_COLOUR.landmark,
    );
  }
}
